import { CanActivateFn, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService, CookieService } from 'riterz-core';
import { environment } from '../../environments/environment';

/**
 * Inverse of `loggedInGuard` — only allows unauthenticated users through.
 *
 * Used on `/auth/login` and `/auth/register` to redirect already-authenticated
 * users back to the app (respects `return_url` query param if present).
 *
 * SSR: returns `true` (cookies unavailable server-side).
 * OAuth redirect: allows through so `AppComponent` can process tokens first.
 */
export const guestGuard: CanActivateFn = (
  route,
  state,
  platformId = inject(PLATFORM_ID),
  router: Router = inject(Router),
  cookieService: CookieService = inject(CookieService),
  authService: AuthService = inject(AuthService),
) => {
  // Skip auth check on server — cookies aren't available during SSR
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  // Allow through if OAuth tokens are in query params (let AppComponent process them first)
  if (route.queryParams['refresh_token'] && route.queryParams['access_token']) {
    return true;
  }

  const isLoggedIn = !!cookieService.get(environment.riterz_access_token_name);

  if (isLoggedIn) {
    const redirectUrl = route.queryParams['redirect_url'];
    if (redirectUrl && authService.isTrustedOauthRedirect(redirectUrl)) {
      authService.redirectToOauthSession(redirectUrl);
      return false;
    }

    const returnUrl = route.queryParams['return_url'];
    router.navigateByUrl(returnUrl || '/');
    return false;
  }

  return true;
};
